/**
 * ============================================================================
 * KbCollectionDialog — Criar/editar coleções da Base de Conhecimento
 * ----------------------------------------------------------------------------
 * Dialog reutilizável. Modo "create" chama useCreateKbCollection; modo "edit"
 * chama useUpdateKbCollection. Slug é auto-derivado do nome se o usuário não
 * digitar manualmente.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateKbCollection, useUpdateKbCollection, type KbCollectionRow,
} from "@/hooks/kb/useKb";
import { InlineNotice } from "@/components/ui/inline-notice";

const EMOJI_SUGGESTIONS = ["📁", "📚", "📖", "📝", "🗂️", "📌", "⭐", "🔧", "🚀", "💡", "🎯", "🧩"];

// Converte texto livre em slug seguro (sem acentos, minúsculo, hífens).
const toSlug = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  spaceId: string;
  parentId?: string | null;
  /** Se fornecido, entra em modo edição. */
  collection?: KbCollectionRow | null;
}

export function KbCollectionDialog({ open, onOpenChange, spaceId, parentId = null, collection }: Props) {
  const isEdit = !!collection;
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [description, setDescription] = useState("");
  const [icon, setIcon] = useState<string>("📁");

  const createMut = useCreateKbCollection();
  const updateMut = useUpdateKbCollection(spaceId);

  // Sincroniza formulário quando abre / muda a coleção-alvo
  useEffect(() => {
    if (!open) return;
    if (collection) {
      setName(collection.name);
      setSlug(collection.slug);
      setSlugDirty(true);
      setDescription(collection.description ?? "");
      setIcon(collection.icon || "📁");
    } else {
      setName(""); setSlug(""); setSlugDirty(false);
      setDescription(""); setIcon("📁");
    }
  }, [open, collection]);

  const effectiveSlug = useMemo(
    () => (slugDirty ? toSlug(slug) : toSlug(name)),
    [name, slug, slugDirty],
  );

  const submitting = createMut.isPending || updateMut.isPending;
  const canSubmit = name.trim().length > 0 && effectiveSlug.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (isEdit && collection) {
      await updateMut.mutateAsync({
        collection_id: collection.id,
        name: name.trim(),
        slug: effectiveSlug,
        description: description.trim() || null,
        icon,
      });
    } else {
      await createMut.mutateAsync({
        space_id: spaceId,
        name: name.trim(),
        slug: effectiveSlug,
        parent_id: parentId,
        icon,
        description: description.trim() || null,
      });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar coleção" : "Nova coleção"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Ícone</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {EMOJI_SUGGESTIONS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => setIcon(e)}
                  className={`h-9 w-9 rounded-md border text-lg transition-colors ${
                    icon === e ? "border-primary bg-primary/10" : "border-border hover:bg-muted"
                  }`}
                  aria-label={`Selecionar ícone ${e}`}
                >
                  {e}
                </button>
              ))}
              <Input
                value={icon}
                onChange={(e) => setIcon(e.target.value.slice(0, 4))}
                className="h-9 w-16 text-center"
                aria-label="Ícone personalizado"
              />
            </div>
          </div>

          <div>
            <Label>Nome *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="Ex.: Instalação, Configuração, FAQ"
              autoFocus
            />
          </div>

          <div>
            <Label>Slug</Label>
            <Input
              value={slugDirty ? slug : effectiveSlug}
              onChange={(e) => { setSlug(e.target.value); setSlugDirty(true); }}
              maxLength={63}
              placeholder="auto-gerado a partir do nome"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Aparece na URL: <code>/kb/space/collection/<strong>{effectiveSlug || "..."}</strong></code>
            </p>
            {isEdit && (
              <InlineNotice variant="warning" className="mt-2">
                Alterar o slug pode quebrar links externos compartilhados.
              </InlineNotice>
            )}
          </div>

          <div>
            <Label>Descrição</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Opcional — o que agrupa esta coleção?"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? "Salvando..." : isEdit ? "Salvar" : "Criar coleção"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
